import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

interface PartnerOpt {
  id: string;
  code: string;
  name: string;
}

export function GrantPartnerRoleDialog({
  customerId,
  userId,
  userLabel,
  onClose,
  onGranted,
}: {
  customerId: string;
  userId: string;
  userLabel: string;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [partnerId, setPartnerId] = useState('');
  const [role, setRole] = useState<'PARTNER_ADMIN' | 'ORDER_PLACER'>('ORDER_PLACER');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<PartnerOpt[]>('/partners')
      .then((rows) => {
        setPartners(rows);
      })
      .catch(() => setPartners([]));
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/customers/${customerId}/users/${userId}/grant-partner-role`, {
        method: 'POST',
        body: { partnerId, role },
      });
      onGranted();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'role_already_granted') {
        setError(t('customer.grant_partner.already'));
      } else if (err instanceof ApiError && err.code === 'partner_not_found') {
        setError(t('customer.grant_partner.partner_missing'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('customer.grant_partner.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          {t('customer.grant_partner.subtitle')}: <span className="font-medium">{userLabel}</span>
        </p>
        <label className="block space-y-1">
          <span className="font-medium">{t('order.col.partner')}</span>
          <select
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="">{t('common.select')}</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('customer.grant_partner.role')}</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="ORDER_PLACER">{t('role.ORDER_PLACER')}</option>
            <option value="PARTNER_ADMIN">{t('role.PARTNER_ADMIN')}</option>
          </select>
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !partnerId}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('customer.grant_partner.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
